import { Server, Activity, HardDrive, Globe, AlertCircle, Trash2 } from 'lucide-react';
import { Card, StatusBadge, MetricCard, LoadingSpinner, ProgressBar, BackupsCard } from '../components';
import { useStatus } from '../hooks/useStatus';
import { formatDistanceToNow } from 'date-fns';

export function Dashboard() {
  const { data, available, loading, error } = useStatus();

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!available || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">Monitoring data unavailable</h1>
        <p className="text-dark-400 text-sm max-w-sm">
          {error || 'The status feed is stale, missing, or failed validation. Retrying automatically.'}
        </p>
      </div>
    );
  }

  const runningCount = data.containers.filter((c) => c.running).length;
  const healthyApps = data.applications.filter((a) => a.status === 'healthy').length;
  const services = data.services ?? [];
  const activeServices = services.filter((svc) => svc.active).length;
  // Non-Docker droplets (e.g. SFO2) have no containers/apps to count —
  // headline their systemd services instead of a meaningless 0/0.
  const showServicesCard = data.containers.length === 0 && services.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-dark-400 mt-1">
            Updated {formatDistanceToNow(new Date(data.generatedAt), { addSuffix: true })}
          </p>
        </div>
      </div>

      {/* Top-level metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {showServicesCard ? (
          <MetricCard
            title="Services"
            value={`${activeServices}/${services.length}`}
            icon={Server}
            color={activeServices === services.length ? 'green' : 'yellow'}
          />
        ) : (
          <MetricCard
            title="Containers"
            value={`${runningCount}/${data.containers.length}`}
            icon={Server}
            color={runningCount === data.containers.length ? 'green' : 'yellow'}
          />
        )}
        {data.applications.length > 0 && (
          <MetricCard
            title="Applications"
            value={`${healthyApps}/${data.applications.length}`}
            icon={Globe}
            color={healthyApps === data.applications.length ? 'green' : 'yellow'}
          />
        )}
        <MetricCard
          title="Root Disk"
          value={`${data.resources.diskRootPercent}%`}
          icon={HardDrive}
          color={data.resources.diskRootPercent > 85 ? 'yellow' : 'green'}
        />
        <MetricCard
          title="Memory"
          value={`${data.resources.memoryUsedPercent}%`}
          icon={Activity}
          color={data.resources.memoryUsedPercent > 90 ? 'yellow' : 'green'}
        />
      </div>

      {/* Resource bars */}
      <Card>
        <h2 className="text-lg font-semibold text-white mb-4">System Resources</h2>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm text-dark-400 mb-1">
              <span>Root disk</span>
              <span>{data.resources.diskRootPercent}%</span>
            </div>
            <ProgressBar value={data.resources.diskRootPercent} showLabel={false} />
          </div>
          {data.resources.blockStoragePercent !== null && (
            <div>
              <div className="flex justify-between text-sm text-dark-400 mb-1">
                <span>Block storage</span>
                <span>{data.resources.blockStoragePercent}%</span>
              </div>
              <ProgressBar value={data.resources.blockStoragePercent} showLabel={false} />
            </div>
          )}
          <div>
            <div className="flex justify-between text-sm text-dark-400 mb-1">
              <span>Memory</span>
              <span>{data.resources.memoryUsedPercent}%</span>
            </div>
            <ProgressBar value={data.resources.memoryUsedPercent} showLabel={false} />
          </div>
          <div className="text-sm text-dark-400">Load average (1m): {data.resources.loadAverage1m}</div>
        </div>
      </Card>

      {/* Disk auto-cleanup — read-only visibility into aggressive-disk-monitor.sh,
          which already runs tiered cleanup automatically every 15 minutes;
          this card has no controls of its own (SSH-only, matching every
          other lifecycle operation retired from this dashboard). */}
      {data.diskCleanup && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-dark-400" />
              Disk Auto-Cleanup
            </h2>
            <StatusBadge status={data.diskCleanup.enabled ? 'active' : 'stopped'} />
          </div>
          <div className="text-sm text-dark-400 space-y-1">
            <div>
              Last triggered:{' '}
              {data.diskCleanup.lastRunAt
                ? `${data.diskCleanup.lastRunAt} (tier ${data.diskCleanup.lastRunTier}, freed ${data.diskCleanup.lastRunFreedPercent}%)`
                : 'never — disk has stayed under the 70% threshold'}
            </div>
            {data.diskCleanup.orphanVolumesPending !== undefined && (
              <div>
                Orphan volumes:{' '}
                {data.diskCleanup.orphanVolumesPending ?? 'unknown'} waiting
                {' · last pruned '}
                {data.diskCleanup.orphanVolumesLastPrunedAt
                  ? `${data.diskCleanup.orphanVolumesLastPrunedAt} (${data.diskCleanup.orphanVolumesLastPrunedCount ?? '?'} removed)`
                  : 'never'}
              </div>
            )}
            <div className="text-xs text-dark-500">
              Runs automatically every 15 minutes at 70/80/90/95% thresholds. Unused anonymous Docker volumes older than 24h are removed every run, regardless of disk usage. No manual controls here — see SERVER_DETAILS.md for the runbook.
            </div>
          </div>
        </Card>
      )}

      {/* Portfolio backups: read-only status; restores and backup management are SSH-only. */}
      {data.backups && <BackupsCard backups={data.backups} />}

      {/* System services (secondary droplets only) */}
      {data.services && data.services.length > 0 && (
        <Card>
          <h2 className="text-lg font-semibold text-white mb-4">Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.services.map((svc) => (
              <div key={svc.name} className="flex items-center justify-between bg-dark-800 rounded-lg px-4 py-3">
                <span className="text-white text-sm font-medium truncate">{svc.name}</span>
                <StatusBadge status={svc.active ? 'active' : svc.state === 'failed' ? 'down' : 'stopped'} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Applications */}
      {data.applications.length > 0 && (
      <Card>
        <h2 className="text-lg font-semibold text-white mb-4">Applications</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.applications.map((app) => (
            <div
              key={app.application}
              className="flex items-center justify-between bg-dark-800 rounded-lg px-4 py-3"
            >
              <span className="text-white text-sm font-medium">{app.application}</span>
              <StatusBadge status={app.status === 'healthy' ? 'healthy' : 'down'} />
            </div>
          ))}
        </div>
      </Card>
      )}

      {/* Containers (read-only — lifecycle actions are SSH-only) */}
      {data.containers.length > 0 && (
      <Card>
        <h2 className="text-lg font-semibold text-white mb-4">Containers</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.containers.map((c) => (
            <div key={c.name} className="flex items-center justify-between bg-dark-800 rounded-lg px-4 py-3">
              <span className="text-white text-sm font-medium truncate">{c.name}</span>
              <StatusBadge status={!c.running ? 'stopped' : c.health === 'unhealthy' ? 'warning' : 'healthy'} />
            </div>
          ))}
        </div>
      </Card>
      )}

      {/* Nginx vhosts */}
      {data.nginx.length > 0 && (
      <Card>
        <h2 className="text-lg font-semibold text-white mb-4">Nginx Sites</h2>
        <div className="flex flex-wrap gap-2">
          {data.nginx.map((v) => (
            <span
              key={v.vhost}
              className="text-xs bg-dark-800 text-dark-300 rounded-full px-3 py-1"
              title={v.status}
            >
              {v.vhost}
            </span>
          ))}
        </div>
      </Card>
      )}
    </div>
  );
}
